import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { unifiedApiService } from '../../services/unified';
import { env } from '../../config/env';

export interface Video {
  id: string;
  user_id: string;
  filename: string;
  s3_url: string;
  duration_seconds: number;
  uploaded_at: string;
}

/**
 * Query hook for getting user videos
 */
export const useUserVideos = () => {
  return useQuery({
    queryKey: ['videos', 'user'],
    queryFn: async (): Promise<Video[]> => {
      const response = await unifiedApiService.getAxiosInstance().get('/api/user/videos');
      return response.data;
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

/**
 * Mutation hook for uploading video
 */
export const useUploadVideo = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ file, onProgress }: { file: File; onProgress?: (progress: number) => void }) => {
      const formData = new FormData();
      formData.append('video', file);

      // Use fetch API instead of Axios to avoid header issues
      const token = localStorage.getItem('auth_token');
      
      // Simple progress simulation since fetch doesn't support upload progress easily
      if (onProgress) {
        onProgress(10);
      }

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 120000); // 2 minute timeout

      try {
        const response = await fetch(`${env.API_BASE_URL}/api/user/videos`, {
          method: 'POST',
          headers: {
            'Authorization': token ? `Bearer ${token}` : '',
            // Don't set Content-Type - browser will set it with boundary
          },
          body: formData,
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        if (onProgress) {
          onProgress(90);
        }

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.error || 'Upload failed');
        }

        const result = await response.json();
        
        if (onProgress) {
          onProgress(100);
        }

        return result;
      } catch (error: any) {
        clearTimeout(timeoutId);
        if (error.name === 'AbortError') {
          throw new Error('Upload timeout - video file may be too large or connection is slow');
        }
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['videos', 'user'] });
    },
  });
};

/**
 * Mutation hook for deleting video
 */
export const useDeleteVideo = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (videoId: string) => {
      const response = await unifiedApiService.getAxiosInstance().delete(`/api/user/videos/${videoId}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['videos', 'user'] });
    },
    onError: (error: any) => {
      console.error('Video deletion failed:', error);
      throw error;
    },
  });
};
